
import { useRef } from "react";
import { useInView } from "framer-motion";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const tiers = [
  {
    name: "Basic",
    id: "basic",
    price: 150,
    photos: "Up to 100",
    photoUpgrade: { cost: 30, maxPhotos: 175 },
    videos: "No videos",
    resolution: "1080p",
    transitions: "Only Fade",
    resolutionUpgrades: [
      { resolution: "2.7k", price: 15 },
      { resolution: "4k", price: 35 },
      { resolution: "8k", price: 55 },
    ],
  },
  {
    name: "Pro",
    id: "pro",
    price: 175,
    photos: "Up to 175",
    photoUpgrade: { cost: 45, maxPhotos: 225 },
    videos: "Two 30-second videos",
    resolution: "2.7k",
    transitions: "Any",
    resolutionUpgrades: [
      { resolution: "4k", price: 30 },
      { resolution: "8k", price: 50 },
    ],
  },
  {
    name: "Max",
    id: "max",
    price: 220,
    photos: "Up to 225",
    photoUpgrade: { cost: 30, maxPhotos: 265 },
    videos: "Three 30-second videos",
    resolution: "2.7k",
    transitions: "Any",
    resolutionUpgrades: [
      { resolution: "4k", price: 20 },
      { resolution: "8k", price: 30 },
    ],
  },
  {
    name: "Ultra",
    id: "ultra",
    price: 250,
    photos: "Unlimited",
    videos: "Unlimited videos",
    resolution: "4k",
    transitions: "Any",
    resolutionUpgrades: [
      { resolution: "8k", price: 15 },
    ],
  },
];

const PricingCard = ({ tier, index }: { tier: any; index: number }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const handleOrderClick = () => {
    const orderSection = document.getElementById('order');
    const packageSelect = document.getElementById('package') as HTMLSelectElement;
    if (orderSection) {
      orderSection.scrollIntoView({ behavior: 'smooth' });
      if (packageSelect) {
        packageSelect.value = tier.id;
        // Trigger change event to update the form state
        const event = new Event('change', { bubbles: true });
        packageSelect.dispatchEvent(event);
      }
    }
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      className="bg-white rounded-lg shadow-xl p-6 transform hover:scale-105 transition-transform duration-300 flex flex-col justify-between h-full relative"
    >
      {tier.id === 'ultra' && (
        <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-navy-600 text-white flex items-center gap-1 px-3 py-1">
          <Star className="w-3 h-3 fill-current" />
          Most Popular
        </Badge>
      )}
      <div>
        <h3 className="text-2xl font-bold text-navy-800 mb-4">{tier.name}</h3>
        <p className="text-4xl font-bold text-navy-600 mb-6">${tier.price}</p>
        <ul className="space-y-3 mb-6 text-gray-600">
          <li>{tier.photos} Photos</li>
          {tier.photoUpgrade && (
            <li className="text-sm">Photo upgrade: +${tier.photoUpgrade.cost} (Max {tier.photoUpgrade.maxPhotos})</li>
          )}
          <li>{tier.videos}</li>
          <li>Base Resolution: {tier.resolution}</li>
          <li>Transitions: {tier.transitions}</li>
          {tier.resolutionUpgrades && tier.resolutionUpgrades.length > 0 && (
            <li className="text-sm">
              Available upgrades:
              <ul className="ml-4 mt-1">
                {tier.resolutionUpgrades.map((upgrade: any) => (
                  <li key={upgrade.resolution}>
                    {upgrade.resolution}: +${upgrade.price}
                  </li>
                ))}
              </ul>
            </li>
          )}
        </ul>
      </div>
      <div className="mt-auto">
        <button
          onClick={handleOrderClick}
          className="block w-full bg-navy-600 text-white text-center py-3 rounded-lg hover:bg-navy-700 transition-colors"
        >
          Order Now
        </button>
      </div>
    </motion.div>
  );
};

export const PricingSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  return (
    <section id="pricing" className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-navy-800 mb-4">
            Choose Your Package
          </h2>
          <p className="text-xl text-gray-600 mb-2">
            Select the perfect package for your needs
          </p>
          <p className="text-lg text-gray-600 font-semibold">
            Orders placed within two weeks of date that's needed will have a $20 up charge
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {tiers.map((tier, index) => (
            <PricingCard key={tier.name} tier={tier} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
